/**
 * PropertyCoveragePanel — model-wide property coverage for one audit.
 *
 * Descriptive only: which property names exist with a non-empty value,
 * per IFC class. No pass/fail, no severity. Data comes from
 * GET /api/audits/{auditId}/property-coverage (stored during the audit
 * element loop). Container/table styling copied from
 * components/CategoryBreakdown.js (card-elevated wrapper + muted rows).
 */
import React, { useEffect, useMemo, useState } from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { apiFetch } from '../lib/supabase';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const COLUMNS = [
  { key: 'name', label: 'Property' },
  { key: 'present', label: 'Present' },
  { key: 'total', label: 'Total' },
  { key: 'pct', label: 'Coverage %' },
  { key: 'classes', label: 'Classes affected' },
];

export function PropertyCoveragePanel({ auditId }) {
  const [data, setData] = useState(null);
  const [missing, setMissing] = useState(false);
  const [sortKey, setSortKey] = useState('pct');
  const [sortAsc, setSortAsc] = useState(true); // least-populated first
  const [expanded, setExpanded] = useState(null);

  useEffect(() => {
    if (!auditId) return;
    let alive = true;
    (async () => {
      try {
        const resp = await apiFetch(`${API_URL}/api/audits/${auditId}/property-coverage`);
        if (!alive) return;
        if (resp.status === 404) { setMissing(true); return; }
        if (!resp.ok) return;
        setData(await resp.json());
      } catch { /* panel is optional — stay hidden on error */ }
    })();
    return () => { alive = false; };
  }, [auditId]);

  const rows = useMemo(() => {
    if (!data?.by_property) return [];
    const list = Object.entries(data.by_property).map(([name, v]) => ({
      name,
      present: v.present || 0,
      total: v.total || 0,
      pct: v.total > 0 ? (v.present / v.total) * 100 : 0,
      classes: Object.keys(v.by_class || {}).length,
      byClass: v.by_class || {},
    }));
    list.sort((a, b) => {
      const va = a[sortKey]; const vb = b[sortKey];
      const cmp = typeof va === 'string' ? va.localeCompare(vb) : va - vb;
      return sortAsc ? cmp : -cmp;
    });
    return list;
  }, [data, sortKey, sortAsc]);

  if (missing || !data) return null;

  const toggleSort = (key) => {
    if (key === sortKey) setSortAsc(!sortAsc);
    else { setSortKey(key); setSortAsc(true); }
  };

  return (
    <div className="card-elevated p-4 mb-6" data-testid="property-coverage-panel">
      <div className="flex items-baseline justify-between flex-wrap gap-2 mb-1">
        <h3 className="text-sm font-semibold">Property coverage (model-wide)</h3>
        <p className="text-[11px] text-muted-foreground">
          {data.total_elements} elements · {rows.length} property names
          {data.truncated?.dropped > 0 &&
            ` · showing top ${data.truncated.cap}, ${data.truncated.dropped} less-frequent names not shown`}
        </p>
      </div>
      <p className="text-[11px] text-muted-foreground mb-3">
        Descriptive only — how many elements carry each property with a
        non-empty value. Click a row for the per-class breakdown.
      </p>
      <p
        className="text-[11px] text-muted-foreground/70 mb-3 -mt-2"
        title="Share of ALL elements carrying this property, not scoped to the classes it applies to — see Classes affected."
      >
        Coverage % is share of ALL elements — not scoped to the classes a property applies to (see Classes affected).
      </p>
      <table className="w-full text-xs">
        <thead>
          <tr className="text-left text-muted-foreground border-b border-border">
            {COLUMNS.map((c) => (
              <th
                key={c.key}
                className="py-1.5 pr-3 cursor-pointer select-none hover:text-foreground"
                onClick={() => toggleSort(c.key)}
                data-testid={`coverage-sort-${c.key}`}
              >
                {c.label}{sortKey === c.key ? (sortAsc ? ' ↑' : ' ↓') : ''}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => {
            const isOpen = expanded === r.name;
            return (
              <React.Fragment key={r.name}>
                <tr
                  className="border-b border-border/50 hover:bg-muted/30 cursor-pointer"
                  onClick={() => setExpanded(isOpen ? null : r.name)}
                  data-testid={`coverage-row-${r.name}`}
                >
                  <td className="py-1.5 pr-3 font-medium">
                    <span className="inline-flex items-center gap-1">
                      {isOpen ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
                      {r.name}
                    </span>
                  </td>
                  <td className="py-1.5 pr-3 tabular-nums">{r.present}</td>
                  <td className="py-1.5 pr-3 tabular-nums">{r.total}</td>
                  <td className="py-1.5 pr-3 tabular-nums">{r.pct.toFixed(1)}%</td>
                  <td className="py-1.5 pr-3 tabular-nums">{r.classes}</td>
                </tr>
                {isOpen && (
                  <tr className="bg-muted/20" data-testid={`coverage-detail-${r.name}`}>
                    <td colSpan={5} className="py-2 pl-6 pr-3">
                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-1">
                        {Object.entries(r.byClass).map(([cls, v]) => (
                          <div key={cls} className="flex justify-between gap-2 text-[11px]">
                            <span className="text-muted-foreground">{cls}</span>
                            <span className="tabular-nums">
                              {v.present}/{v.total}
                              {' '}({v.total > 0 ? Math.round((v.present / v.total) * 100) : 0}%)
                            </span>
                          </div>
                        ))}
                      </div>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
